import type { Attachment } from "@/admin/hooks/use-subject-attachments";
import {
  MEDIA_GROUP_PREFIX,
  evidenceGroupLabel,
  resolveEvidenceCategoryKey,
} from "@/shared/components/evidence/evidence-grouping";

/**
 * A file filed against a record can be deleted here. One that is only
 * referenced from a record's own fields (e.g. the Proof of Purchase captured
 * while registering) carries no subject: the backend refuses to delete it
 * through this card, though it can still be relabelled by naming the record.
 */
export function isFiled(att: Pick<Attachment, "is_deleted" | "subject_type">): boolean {
  return !att.is_deleted && !!att.subject_type;
}

export interface CategoryGroup {
  key: string;
  label: string;
  rows: Attachment[];
}

/**
 * Bucket documents by category, as the claim Evidence card does. A section
 * reads as someone typed it ("Proof of Purchase"), and a slug for the same
 * thing ("proof_of_purchase") lands in the same section. Labelled sections
 * come first, alphabetically; files nobody labelled fall back to their media
 * type (Photos, Documents) and come last.
 */
export function groupRowsByCategory(rows: Attachment[]): CategoryGroup[] {
  const typedLabels = new Map<string, string>();
  for (const att of rows) {
    const key = resolveEvidenceCategoryKey(att);
    const typed = att.category?.trim();
    if (typed && typed !== key && !typedLabels.has(key)) typedLabels.set(key, typed);
  }

  const byKey = new Map<string, CategoryGroup>();
  for (const att of rows) {
    const key = resolveEvidenceCategoryKey(att);
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: evidenceGroupLabel(key, typedLabels), rows: [] };
      byKey.set(key, group);
    }
    group.rows.push(att);
  }

  const unlabelled = (g: CategoryGroup) => Number(g.key.startsWith(MEDIA_GROUP_PREFIX));
  return [...byKey.values()].sort(
    (a, b) => unlabelled(a) - unlabelled(b) || a.label.localeCompare(b.label),
  );
}

/** The labels already used on a record, offered when labelling a file. */
export function labelSuggestions(groups: CategoryGroup[]): string[] {
  return groups
    .filter((g) => !g.key.startsWith(MEDIA_GROUP_PREFIX))
    .map((g) => g.label);
}
